import type {
  Lesson,
  RepoState,
  QuizQuestion,
  GlossaryItem,
  CheatsheetItem,
} from "../../lib/types";

export const LESSON_UNDOING: Lesson = {
  courseId: "undoing",
  courseTitle: "Undoing & recovering",
  id: "lesson-1",
  title: "Undoing & recovering",
  subtitle: "Lesson 6 of 6",
  steps: [
    {
      id: "intro",
      shortTitle: "The safety net",
      shortSub: "Why you (almost) can't lose work",
      eyebrow: "Step 1 of 6",
      title: "Git keeps a safety net at every layer.",
      sub: "Working tree, staging area, commits, and the reflog. Each layer has its own undo command — once you know which layer your mistake landed in, the fix is short and boring.",
      kind: "intro",
    },
    {
      id: "restore",
      shortTitle: "Restore a file",
      shortSub: "Throw away unstaged edits",
      eyebrow: "Step 2 of 6",
      title: "You edited App.jsx. You don't want the edits.",
      sub: "Working-tree changes that you haven't staged or committed are the cheapest to undo. `git restore <file>` replaces the file in your working tree with the last committed version. No history changes — there's no history yet for these edits.",
      kind: "sandbox",
      goal: "restore-file",
    },
    {
      id: "reset",
      shortTitle: "Reset a bad commit",
      shortSub: "Move HEAD backwards",
      eyebrow: "Step 3 of 6",
      title: "You committed too early. Pretend it never happened.",
      sub: "The last commit on main is a typo. Because it's only on your laptop — you haven't pushed — you can rewrite history. `git reset` moves the branch backwards. Three flavors: --soft (keep staged), --mixed (keep working tree), --hard (throw everything away).",
      kind: "sandbox",
      goal: "reset-commit",
    },
    {
      id: "revert",
      shortTitle: "Revert a shared commit",
      shortSub: "Add an inverse, keep history",
      eyebrow: "Step 4 of 6",
      title: "Same bad commit — but this time it's already shared with the team.",
      sub: "Once a commit is on origin and someone else has pulled it, `git reset` is dangerous: rewriting history other people have splits the repo. Instead, `git revert` creates a NEW commit that undoes the bad one. History is preserved; the bad change is neutralized.",
      kind: "sandbox",
      goal: "revert-commit",
    },
    {
      id: "reflog",
      shortTitle: "Recover with reflog",
      shortSub: "Find commits you 'lost'",
      eyebrow: "Step 5 of 6",
      title: "You ran `git reset --hard` and your commits seem gone. They aren't.",
      sub: "Git keeps a private log of every HEAD movement for 90 days. It's called the reflog. As long as a commit had a name (you committed it), you can find it again and pull it back with `git reset --hard HEAD@{N}`.",
      kind: "reflog-sandbox",
      goal: "reflog-recover",
    },
    {
      id: "wrap",
      shortTitle: "Quiz & recap",
      shortSub: "Knowledge check",
      eyebrow: "Wrap-up",
      title: "Four undos, four layers.",
      sub: "Restore (working tree), reset (commits on your laptop), revert (commits shared with the team), reflog (the 'I really did mess up' parachute). Quick check below, then cheatsheet + glossary.",
      kind: "wrap",
    },
  ],
};


export const QUIZ_QUESTIONS_UNDOING: QuizQuestion[] = [
  {
    q: "You edited App.jsx but haven't staged or committed yet. You decide the edit was a mistake. Cheapest undo?",
    options: [
      { t: "git reset --hard HEAD", correct: false },
      { t: "git restore App.jsx", correct: true },
      { t: "git revert HEAD", correct: false },
      { t: "Delete the file and re-clone the repo", correct: false },
    ],
    why: "Right — `git restore` overwrites the working-tree file with the committed version. Nothing was saved yet, so nothing needs to be recovered.",
    wrongWhy:
      "`git restore <file>` is the right tool — it's working-tree-only and doesn't touch history. The other options either do more than you need or are catastrophic overkill.",
  },
  {
    q: "You committed a typo and haven't pushed yet. You want the commit gone like it never happened. What do you run?",
    options: [
      { t: "git revert HEAD", correct: false },
      { t: "git restore .", correct: false },
      { t: "git reset --hard HEAD~1", correct: true },
      { t: "git push --force-with-lease", correct: false },
    ],
    why: "Yes — `git reset --hard HEAD~1` moves main back one commit and resets the working tree. Safe because the bad commit was only on your laptop.",
    wrongWhy:
      "Because it's not pushed yet, you can rewrite history. `git reset --hard HEAD~1` moves the branch back one commit and the working tree with it. Revert would also work but it leaves the bad commit visible in history.",
  },
  {
    q: "Same bad commit — but you already pushed and a teammate pulled it. Now what?",
    options: [
      { t: "git reset --hard HEAD~1 then git push --force", correct: false },
      { t: "git revert HEAD", correct: true },
      { t: "Delete the branch on GitHub and recreate it", correct: false },
      { t: "Open a support ticket with GitHub", correct: false },
    ],
    why: "Right — `git revert` creates a new commit that inverts the bad one. History is preserved, your teammate's clone is fine, and the bad change is neutralized.",
    wrongWhy:
      "Once a commit is shared, rewriting history hurts everyone who's already pulled. `git revert HEAD` adds a NEW commit that undoes the bad one — safe for shared history.",
  },
  {
    q: "You ran `git reset --hard HEAD~3` and now your last three commits look gone. Are they recoverable?",
    options: [
      { t: "No, --hard is permanent", correct: false },
      { t: "Only if you committed them in the last hour", correct: false },
      { t: "Yes — `git reflog` lists every HEAD move, and you can `git reset --hard HEAD@{N}` to one of the earlier states", correct: true },
      { t: "Only if someone else still has them on their laptop", correct: false },
    ],
    why: "Exactly. The reflog tracks every HEAD movement for ~90 days. Any commit you'd made still has an entry — find it, reset to it, recover.",
    wrongWhy:
      "Git almost never throws committed work away. The reflog keeps a record of every HEAD move for 90 days; `git reflog` shows it, and you can `git reset --hard HEAD@{N}` to roll back to any prior state.",
  },
];

export const GLOSSARY_UNDOING: GlossaryItem[] = [
  {
    term: "git restore",
    def: "Overwrite a file in the working tree with its last committed version. Cheap, local, doesn't touch history.",
  },
  {
    term: "git reset",
    def: "Move the current branch pointer to a different commit. Comes in three modes: --soft (history-only), --mixed (clears index), --hard (clears index + working tree).",
  },
  {
    term: "git revert",
    def: "Create a new commit that inverts the changes from a previous commit. Safe for shared history because nothing is rewritten — the bad commit and its inverse both stay.",
  },
  {
    term: "Reflog",
    def: "Git's private log of every HEAD movement. Persists for ~90 days even after `reset --hard`. Use `git reflog` to list, then `git reset --hard HEAD@{N}` to rewind.",
  },
  {
    term: "Orphaned commit",
    def: "A commit that no branch or tag points at anymore. Still in the repo for ~90 days — the reflog can resurrect it.",
  },
  {
    term: "HEAD@{N}",
    def: "Reflog notation for 'where HEAD was N moves ago'. `HEAD@{0}` is current; `HEAD@{1}` is one HEAD-move back; and so on.",
  },
  {
    term: "Rewriting history",
    def: "Anything that changes commits already made — like `git reset` on a shared branch, or `git rebase`. Safe for commits only on your laptop; dangerous once others have pulled them.",
  },
];

export const CHEATSHEET_UNDOING: CheatsheetItem[] = [
  { cmd: "git restore <file>", desc: "Throw away unstaged edits to a file. Working tree only — never touches history." },
  { cmd: "git restore --staged <file>", desc: "Unstage a file but keep your working-tree edits. Opposite of `git add`." },
  { cmd: "git reset --soft HEAD~1", desc: "Move branch back one commit, keep the changes staged. Useful for redoing the last commit." },
  { cmd: "git reset --mixed HEAD~1", desc: "Move branch back one commit, keep changes in the working tree but unstaged. (--mixed is the default.)" },
  { cmd: "git reset --hard HEAD~1", desc: "Move branch back one commit AND throw away staged + working changes. Destructive — only safe on unpushed commits." },
  { cmd: "git revert <commit>", desc: "Create a new commit that inverts <commit>. Safe undo for commits already pushed." },
  { cmd: "git reflog", desc: "List every HEAD movement (commit, reset, switch, merge…). Your recovery log." },
  { cmd: "git reset --hard HEAD@{N}", desc: "Rewind to the state HEAD had N moves ago. Resurrects commits a `reset --hard` 'lost'." },
];

// ----------------- Initial repo state -----------------

const APP_JSX_GOOD = `import Header from './Header';

export default function App() {
  return (
    <div className="app">
      <Header />
      <main>
        <h1>The Atlas operating system</h1>
        <p>The single workspace your team actually opens every morning.</p>
      </main>
    </div>
  );
}
`;

const APP_JSX_OOPS = `import Header from './Header';

export default function App() {
  return (
    <div className="app">
      <Header />
      <main>
        <h1>The Atlas operatign system</h1>
        <p>The single workspace your team actaully opens every morning.</p>
      </main>
    </div>
  );
}
`;

const README_MD = `# Atlas

Internal business operating system.
`;

/**
 * Starting state.
 *
 * History on main:
 *   a1b2c3d  Initial commit
 *   e4f5a6b  Add Header component
 *   c7d8e9f  Wire up homepage layout            ← "good" tip for revert/reset target
 *   o0o0o0o  Oops: typo in copy                 ← the bad commit (current HEAD)
 *
 * Each commit carries a `snapshot` so reset --hard and revert can replay file content.
 */
export const INITIAL_REPO_UNDOING: RepoState = {
  commits: [
    {
      id: "a1b2c3d",
      msg: "Initial commit",
      branch: "main",
      parent: null,
      x: 0,
      snapshot: { "App.jsx": APP_JSX_GOOD, "README.md": README_MD },
    },
    {
      id: "e4f5a6b",
      msg: "Add Header component",
      branch: "main",
      parent: "a1b2c3d",
      x: 1,
      snapshot: { "App.jsx": APP_JSX_GOOD, "README.md": README_MD },
    },
    {
      id: "c7d8e9f",
      msg: "Wire up homepage layout",
      branch: "main",
      parent: "e4f5a6b",
      x: 2,
      snapshot: { "App.jsx": APP_JSX_GOOD, "README.md": README_MD },
    },
    {
      id: "o0o0o0o",
      msg: "Oops: typo in copy",
      branch: "main",
      parent: "c7d8e9f",
      x: 3,
      snapshot: { "App.jsx": APP_JSX_OOPS, "README.md": README_MD },
    },
  ],
  branches: { main: "o0o0o0o" },
  head: "main",
  files: {
    "App.jsx": {
      content: APP_JSX_OOPS,
      committed: APP_JSX_OOPS,
      staged: null,
      status: "clean",
    },
    "README.md": {
      content: README_MD,
      committed: README_MD,
      staged: null,
      status: "clean",
    },
  },
  // Pre-seed reflog so HEAD@{N} entries already exist before the learner does anything.
  reflog: [
    { ref: "HEAD", commitId: "a1b2c3d", action: "commit (initial): Initial commit" },
    { ref: "HEAD", commitId: "e4f5a6b", action: "commit: Add Header component" },
    { ref: "HEAD", commitId: "c7d8e9f", action: "commit: Wire up homepage layout" },
    { ref: "HEAD", commitId: "o0o0o0o", action: "commit: Oops: typo in copy" },
  ],
  orphanedCommits: [],
};

export const INITIAL_LOG_UNDOING = [
  {
    kind: "out" as const,
    text: "On branch main. The last commit ('Oops: typo in copy') has typos in the heading and tagline.",
  },
];

/**
 * Pre-baked reset target for the reflog step. The lesson's onEnterStep hook runs
 * `git reset --hard c7d8e9f` to "lose" the Oops commit so the learner can recover it.
 */
export const REFLOG_RESET_TARGET = "c7d8e9f";

/** The good-tip commit (used as the reset target in steps 3 + 4). */
export const GOOD_TIP_COMMIT = "c7d8e9f";

/** The bad commit (used as the revert target in step 4). */
export const OOPS_COMMIT = "o0o0o0o";
